import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[12]

export const KWP_12_INSTALACION_FOTOVOLTAICA: ProgrammaticLandingPageConfig = {
  slug: 'instalacion-fotovoltaica-12-kwp-precio',
  lang: 'es',
  topicId: 'kwp-kosten-12',
  title: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica de 12 kWp? Superficie de tejado, número de ' +
    'paneles, desglose de costes y comparación gratuita para viviendas con bomba de calor.',
  keywords: [
    'instalación fotovoltaica 12 kWp precio',
    'placas solares 12 kWp coste',
    'cuánto cuesta una instalación de 12 kWp',
    '12 kWp instalación solar bomba de calor',
  ],
  eyebrow: 'Tamaño de instalación: 12 kWp',
  h1: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos',
  intro:
    'Una instalación de 12 kWp es adecuada para viviendas unifamiliares más grandes, a menudo ya ' +
    'con bomba de calor. Aquí encontrará valores orientativos sobre superficie de tejado y número ' +
    'de paneles, un desglose de costes y justo debajo una calculadora con el precio medio para ' +
    '12 kWp ya calculado a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp', kwp: 12 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una instalación de 12 kWp?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 12 kWp suele encajar con una vivienda unifamiliar más grande con una ' +
        'bomba de calor como consumidor eléctrico adicional, consumo anual total a menudo entre ' +
        '7.000 y 9.000 kWh. Sin bomba de calor, la mayoría de los hogares se cubren ya con un ' +
        'tamaño menor como 8 o 10 kWp.',
    },
    { type: 'heading', text: 'Tejado y paneles: lo que necesita una instalación de 12 kWp' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: unos ${FACTS.roofAreaM2} m² de tejado y aproximadamente ` +
        `${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp. La producción anual, con ` +
        `orientación sur y sin sombras, ronda los ${FACTS.annualYieldKwh.toLocaleString('es-ES')} kWh. ` +
        'Un cálculo exacto para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Componentes del coste de una instalación fotovoltaica' },
    {
      type: 'list',
      items: [
        'Paneles: el mayor coste individual, normalmente en torno a un tercio del total',
        'Inversor: con 12 kWp suele ser ya necesario un modelo trifásico',
        'Montaje/mano de obra: fijación en el tejado, cableado',
        'Andamiaje: depende de la altura y accesibilidad del tejado',
        'Instalación eléctrica: adaptación del cuadro eléctrico, a menudo pensando en la bomba de calor',
        'Tramitación: registro ante la distribuidora y organismos correspondientes',
      ],
    },
    {
      type: 'paragraph',
      text:
        'A partir de unos 12 kWp conviene revisar con más detalle la acometida eléctrica de la ' +
        'vivienda - según la protección instalada, puede ser necesario un inversor trifásico, lo ' +
        'que hace la instalación eléctrica algo más compleja que en instalaciones más pequeñas.',
    },
    { type: 'heading', text: 'Amortización y autoconsumo' },
    {
      type: 'paragraph',
      text:
        'Con una bomba de calor como consumidor adicional, suele ser posible aprovechar bien la ' +
        'electricidad generada incluso sin una batería grande, especialmente si la bomba de calor ' +
        'funciona sobre todo durante el día. Un cálculo exacto para su tejado lo ofrece la ' +
        'calculadora de dimensionamiento.',
    },
  ],
  faq: [
    {
      q: '¿Es suficiente 12 kWp para bomba de calor y coche eléctrico juntos?',
      a: 'Depende del consumo exacto de ambos equipos - con una bomba de calor eficiente y un uso moderado del coche eléctrico, a menudo sí; con un consumo total elevado, 15 kWp es la opción más segura.',
    },
    {
      q: '¿Cuánta superficie de tejado necesito como mínimo para 12 kWp?',
      a: `Valor orientativo: unos ${FACTS.roofAreaM2} m² de superficie útil de tejado, según el tipo de panel elegido y la orientación.`,
    },
    {
      q: '¿Necesito un inversor trifásico con 12 kWp?',
      a: 'A menudo sí, según la acometida eléctrica existente. Esto lo comprueba la empresa instaladora al elaborar el presupuesto, revisando el cuadro eléctrico.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-10-kwp-precio' },
    { label: 'Instalación fotovoltaica de 15 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-15-kwp-precio' },
    { label: 'Instalación fotovoltaica de 20 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-20-kwp-precio' },
    { label: 'Batería solar de 12 kWh: precio y valores orientativos', href: '/bateria-solar-12-kwh-precio' },
    { label: 'Planificar la instalación paso a paso', href: '/planificar-instalacion-fotovoltaica' },
  ],
}
